import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';
import sucursalXUsuarioService from '../../services/sucursalXUsuarioService';
import { getApiErrorMessage } from '../../utils/apiError';
import { persistGlobalNroSuc, readGlobalNroSuc } from '../../utils/nroSucPreferida';

export interface GranjaSucursalConfigDialogProps {
  open: boolean;
  onClose: () => void;
  /** Se llama tras guardar con el nroSuc elegido. */
  onSaved?: (nroSuc: number) => void;
}

const labelSucursal = (s: SucursalVenta) =>
  `${s.nroSuc} - ${s.nombreSucursal?.trim() || `Sucursal ${s.nroSuc}`}`;

const GranjaSucursalConfigDialog: React.FC<GranjaSucursalConfigDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const {
    sucursales: sucursalesCtx,
    preferidaNro,
    loading: loadingCtx,
    refresh,
  } = useSucursalesUsuarioContext();

  const [opciones, setOpciones] = useState<SucursalVenta[]>([]);
  const [loadingOpciones, setLoadingOpciones] = useState(false);
  const [nroSuc, setNroSuc] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    const actual = preferidaNro && preferidaNro > 0 ? preferidaNro : readGlobalNroSuc() ?? 0;
    setNroSuc(actual);

    if (sucursalesCtx.length > 0) {
      setOpciones(sucursalesCtx);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingOpciones(true);
      try {
        const result = await sucursalesVentaService.getSucursalesVenta({
          pageNumber: 1,
          pageSize: 500,
        });
        if (!cancelled) setOpciones(result.items ?? []);
      } catch (err: unknown) {
        if (!cancelled) {
          setOpciones([]);
          enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar sucursales'), { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingOpciones(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, preferidaNro, sucursalesCtx, enqueueSnackbar]);

  const opcionesOrdenadas = useMemo(
    () => [...opciones].sort((a, b) => a.nroSuc - b.nroSuc),
    [opciones],
  );

  const loading = loadingCtx || loadingOpciones;

  const handleGuardar = async () => {
    if (!nroSuc || nroSuc <= 0) {
      enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      persistGlobalNroSuc(nroSuc);

      const username = user?.username?.trim();
      const asignada = sucursalesCtx.some((s) => Number(s.nroSuc) === nroSuc);
      if (username && asignada) {
        try {
          await sucursalXUsuarioService.setPreferida(nroSuc, username);
          await refresh();
        } catch {
          /* localStorage alcanza para operar en esta sesión */
        }
      }

      enqueueSnackbar(`Sucursal operativa: ${nroSuc}`, { variant: 'success' });
      onSaved?.(nroSuc);
      onClose();
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al guardar la sucursal'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Sucursal operativa</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Elegí la sucursal con la que se registran los movimientos de granja. Si no tenés una
            preferida asignada, configurála acá para poder operar.
          </Typography>
          {loading ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={28} />
            </Stack>
          ) : opcionesOrdenadas.length === 0 ? (
            <Typography variant="body2" color="error">
              No hay sucursales disponibles. Pedile a un administrador que te asigne al menos una.
            </Typography>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="granja-sucursal-config-label">Sucursal</InputLabel>
              <Select
                labelId="granja-sucursal-config-label"
                label="Sucursal"
                value={nroSuc > 0 ? nroSuc : ''}
                onChange={(e) => setNroSuc(Number(e.target.value) || 0)}
              >
                {opcionesOrdenadas.map((s) => (
                  <MenuItem key={s.nroSuc} value={s.nroSuc}>
                    {labelSucursal(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleGuardar()}
          disabled={saving || loading || opcionesOrdenadas.length === 0}
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GranjaSucursalConfigDialog;
